// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * useAddressLookup - V40
 * Hook die adres-autofill afhandelt en statussen bijhoudt.
 * Blokkeert de gebruiker nooit: bij mislukking is handmatige invoer altijd mogelijk.
 */
import { useState, useCallback, useRef, useEffect } from 'react'
import { lookupAddress, type LookupStatus } from '@/services/addressLookup'

interface UseAddressLookupOptions {
  onFound: (street: string, city: string) => void
}

export function useAddressLookup({ onFound }: UseAddressLookupOptions) {
  const [status, setStatus] = useState<LookupStatus>('idle')
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // V48-P1.5 fix: onFound in een ref zodat `lookup` een stabiele identiteit houdt.
  // Zonder dit verandert lookup bij elke parent-rerender → de lookup-effect vuurt opnieuw
  // → "Adres gevonden" knippert (loading→found→loading).
  const onFoundRef = useRef(onFound)
  useEffect(() => { onFoundRef.current = onFound }, [onFound])

  const lookup = useCallback(async (postalCode: string, houseNumber: string) => {
    const pc = postalCode.replace(/\s/g, '')
    const nr = houseNumber.trim()

    // Niet zoeken bij incomplete invoer
    if (pc.length < 6 || !nr) return

    // Annuleer vorige reset-timer
    if (resetTimer.current) clearTimeout(resetTimer.current)

    setStatus('loading')
    const result = await lookupAddress(pc, nr)

    if (result.found) {
      onFoundRef.current(result.street, result.city)
      setStatus('found')
    } else {
      setStatus('not_found')
    }

    // Status na 4 seconden terugzetten naar idle
    resetTimer.current = setTimeout(() => setStatus('idle'), 4000)
  }, [])

  const reset = useCallback(() => {
    if (resetTimer.current) clearTimeout(resetTimer.current)
    setStatus('idle')
  }, [])

  return { status, lookup, reset }
}
